// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — three ships that are three different games, not three numbers.
//
// PATROL  the spine comfortably reaches most of the ship, so the game is PRIORITISATION: which one
//         system do I give up this cycle. The default, and the tutorial's mode.
// HAULER  15 compartments with HELM/COOLANT at one end and SUPPRESSION/PUMP at the other, so the
//         spine can physically never SUSTAIN both halves — the all-five route costs exactly 9 and
//         runs 11 segments (+4.0 heat/s, ~17s before it cooks). Routing becomes RHYTHM: cycle
//         between halves on a beat, and the skill is knowing how long each half can be left dark.
// SCUTTLE no cells, no transit, a hazard every 4-11s, and SEAL warps too — so the ship degrades
//         MONOTONICALLY and your effective reach shrinks even though output never drops. Score-attack.
//
// The layouts are not hand-waved: scripts/paths.mjs enumerates every affordable simple path from the
// reactor and each mode offers 13 distinct system-coverage sets. It also caught the design doc's own
// hand-derived route table claiming a five-system route for cost 7 in Patrol — the real cheapest is 6.

export type CellKind = 'reactor' | 'helm' | 'coolant' | 'pump' | 'suppression' | 'sensors' | 'conduit';

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  cols: number;
  rows: number;
  /** Row-major compartment kinds. */
  kind: CellKind[];
  reactor: number;
  /** Indices holding a battery cell. */
  cells: number[];
  output: number;
  /** Transit %/s while HELM is on the powered prefix. 0 = survival mode. */
  transitRate: number;
  hull: number;
  /** Seconds between hazards: max(floor, start - step * floor(t/20)). */
  hazardStart: number;
  hazardStep: number;
  hazardFloor: number;
  /** Share of hazards that arrive as fire rather than a breach. */
  fireBias: number;
  /** Scuttle: sealing scars the bulkhead too, so the ship only ever gets worse. */
  sealWarps: boolean;
  /** Patrol/Hauler die of drift if they can never reach HELM; Scuttle has no transit to stall. */
  driftClock: boolean;
}

// COOLANT sits directly above the reactor. With it in the far corner instead, Patrol measured HARDER
// than Hauler — 0% wins against 87% — because the cheapest cooling run cost four segments and the
// crew could never both cool and steer. Here the teaching mode is the generous one it should be:
// 10>7>4>1>0 holds COOLANT, HELM and SUPPRESSION on four segments at -0.9/s (the workhorse), and
// 10>7>6>3>0>1>2 lights ALL FIVE systems for five power on six segments at +0.5/s — affordable, and
// slowly fatal. That gap between "can light" and "can sustain" is the whole game, on one screen.
const PATROL_KIND: CellKind[] = [
  'suppression', 'helm', 'sensors',
  'conduit', 'conduit', 'conduit',
  'pump', 'coolant', 'conduit',
  'conduit', 'reactor', 'conduit',
];

// HAULER'S COOLANT IS TWO SEGMENTS OUT AND OFF THE HELM PATH, and getting that placement right took
// three measured attempts. In the top row (five segments out) the ship could not shed heat at all:
// the crew collapsed to reactor+pump inside five seconds and drifted to death on every seed at 4%
// transit. Directly beside the reactor and ON the helm path, the opposite — 13>10>7>4>3>0 held
// COOLANT and HELM together on five segments at -0.2/s, so the crew simply cruised: 100% wins in 128
// seconds on twenty actions, at every party size, the ship never fighting back once.
//
// Moving it OFF the helm path entirely was measured too, and it overshot in the other direction:
// every helm route then baked, the crew could not hold HELM at all, and 97% of runs timed out at
// seven minutes having covered fifteen percent of the crossing. A game you cannot lose and a game
// you cannot finish are the same failure. So COOLANT stays on the path — HELM is holdable — and the
// difficulty comes from the CROSSING BEING LONG (transitRate 0.48, roughly 210 seconds of lit HELM),
// which drags the run deep into the part of the ramp where hazards arrive every five seconds.
const HAULER_KIND: CellKind[] = [
  'helm', 'conduit', 'sensors',
  'conduit', 'conduit', 'conduit',
  'conduit', 'conduit', 'conduit',
  'conduit', 'coolant', 'conduit',
  'suppression', 'reactor', 'pump',
];

export const MODES: Record<string, Mode> = {
  patrol: {
    id: 'patrol',
    name: 'Patrol',
    blurb: 'A short run. The cable reaches most of the ship — choose what to give up.',
    cols: 3,
    rows: 4,
    kind: PATROL_KIND,
    reactor: 10,
    cells: [3, 9],
    output: 7,
    // Measured, not guessed. At 1.35 %/s with hazards every 18s the run finished in 74s having taken
    // EIGHT actions, at a 100% win rate identical for one player and for four — i.e. the ship never
    // once fought back and the party size was decorative. The transit is now longer than the ramp's
    // patient half, so a Patrol run has to survive the part where hazards arrive every five seconds.
    transitRate: 1.25,
    // Patrol is the mode the tutorial runs in and the one a new player meets, so it is the one that
    // has to be winnable. The extra hull is what buys that without softening the ramp everyone else
    // is measured against.
    hull: 115,
    hazardStart: 12.0,
    hazardStep: 2.2,
    hazardFloor: 5.0,
    fireBias: 0.45,
    sealWarps: false,
    driftClock: true,
  },
  hauler: {
    id: 'hauler',
    name: 'Hauler',
    blurb: 'Fifteen compartments, two ends. You cannot sustain both — so learn the rhythm.',
    cols: 3,
    rows: 5,
    kind: HAULER_KIND,
    reactor: 13,
    cells: [3, 8],
    // SEVEN, not nine. Nine power on fifteen compartments meant the crew could always afford HELM,
    // COOLANT, PUMP and SUPPRESSION at once, so the mode's whole premise — two ends you cannot both
    // hold — never actually bound: every run finished in 128 seconds on twenty actions, at 100%, at
    // every party size. At seven, the route gate still finds eight distinct affordable system-sets
    // and NO route that reaches all five, which is the honest expression of what Hauler is.
    output: 7,
    // SLOW ON PURPOSE — this is the hard mode and the run has to last long enough to reach the part
    // of the ramp where hazards arrive every five seconds. At 0.95 the crossing took 105 seconds and
    // every run, at every party size, won without the ship ever fighting back: fifteen actions, 100%.
    // BRACKETED, not guessed. 0.78 measured a 100% win rate in 128 seconds — the ship never fought
    // back. 0.48 measured 0%, dying of drift at 282 seconds with the crossing 71% done. The crossing
    // has to be long enough to drag the run into the part of the ramp where hazards arrive every five
    // seconds, and short enough that a good crew can still finish it.
    transitRate: 0.66,
    hull: 100,
    hazardStart: 13.0,
    hazardStep: 2.2,
    hazardFloor: 5.0,
    fireBias: 0.45,
    sealWarps: false,
    driftClock: true,
  },
  scuttle: {
    id: 'scuttle',
    name: 'Scuttle',
    blurb: 'Nowhere to go. No batteries, and every repair scars the hull. Hold on.',
    cols: 3,
    rows: 4,
    kind: PATROL_KIND,
    reactor: 10,
    cells: [],
    output: 8,
    transitRate: 0,
    hull: 100,
    hazardStart: 9.0,
    hazardStep: 1.8,
    hazardFloor: 2.5,
    // FIRE-WEIGHTED. With the same 45% mix as the other two, water took 100% of Scuttle's losses —
    // a single cause taking every run means the other four systems are decoration, which is exactly
    // what the loss-cause spread assertion exists to catch. Fire is also the right antagonist for a
    // ship with no pumps to spare: it is the hazard that punishes a long cable.
    fireBias: 0.62,
    sealWarps: true,
    driftClock: false,
  },
};

export const MODE_IDS = ['patrol', 'hauler', 'scuttle'] as const;
export const DEFAULT_MODE = 'patrol';

/**
 * Validate an id that came off the wire or out of localStorage.
 *
 * `Object.hasOwn`, not `MODES[id] ||` — a bare index lets "constructor" and "__proto__" through, and
 * an unknown id must fall back rather than reach the generator as undefined.
 */
export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}

/**
 * Orthogonal neighbours of a cell. Adjacency is orthogonal ONLY.
 *
 * Cached per mode, and the returned array must be treated as READ-ONLY. This is called several times
 * per compartment per tick, and the sim runs millions of ticks while tuning; allocating four fresh
 * arrays per cell per tick was measurably the difference between a sim you run once and a sim you
 * run thirty times.
 */
const ADJ = new WeakMap<Mode, readonly number[][]>();

export function neighbours(m: Mode, i: number): readonly number[] {
  let table = ADJ.get(m);
  if (!table) {
    const n = m.cols * m.rows;
    table = Array.from({ length: n }, (_, k) => {
      const x = k % m.cols;
      const y = (k / m.cols) | 0;
      const out: number[] = [];
      if (y > 0) out.push(k - m.cols);
      if (y < m.rows - 1) out.push(k + m.cols);
      if (x > 0) out.push(k - 1);
      if (x < m.cols - 1) out.push(k + 1);
      return out;
    });
    ADJ.set(m, table);
  }
  return table[i];
}

export const cellCount = (m: Mode): number => m.cols * m.rows;
